import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';

import { getCompoundEntityRef, parseEntityRef } from '@backstage/catalog-model';
import { capitalize, upperFirst } from 'es-toolkit';

export type KeyRoleOptions = {
  /**
   * icon to use in the UI
   */
  icon?: string;
  /**
   * the entity kind of this role
   */
  kind?: string;
  label?: string;

  noValueLabel?: string;

  /**
   * if true, the default (backstage) relation handling will be used, defaults to false
   */
  skipRelationProcessing?: boolean;
};

/**
 * Return the names of the fields of <T>, where the fields' type matches <Type>.
 *
 *     @param T the type to extract the filed names from
 *     @param Type the type to filter the fields
 */
type NamesOfFieldsWithType<T, Type> = keyof {
  [K in keyof T as T[K] extends Type ? K : never]: undefined;
};

export class KeyRole<
  ENTITY extends Entity,
  // only allow field names from the entity::spec that are of type string
  FIELD extends NamesOfFieldsWithType<Required<ENTITY['spec']>, string>
  & string,
> {
  readonly options: Required<KeyRoleOptions>;

  constructor(
    readonly matcher: (entity: Entity) => entity is ENTITY,
    readonly field: FIELD,
    options?: KeyRoleOptions,
  ) {
    const label =
      options?.label
      ?? field
        // https://stackoverflow.com/a/7599674/1666829
        .split(/(?<=[a-z])(?=[A-Z])/)
        .map(field => capitalize(field))
        .join(' ');

    this.options = {
      icon: `/icons/keyRoles/${upperFirst(field)}.svg`,
      kind: 'User',
      label,
      noValueLabel: `No ${label}`,
      skipRelationProcessing: false,
      ...options,
    };
  }

  /**
   * @param entity the entity to extract the ref from
   * @return the ref for this role or `undefined` if ref cannot be extracted
   */
  extractRef = (entity: Entity): CompoundEntityRef | undefined => {
    if (!this.matcher(entity)) {
      return undefined;
    }
    const ref = entity.spec?.[this.field];
    if (typeof ref !== 'string') {
      return undefined;
    }
    const entityRef = getCompoundEntityRef(entity);
    return parseEntityRef(ref, {
      defaultKind: this.options.kind,
      defaultNamespace: entityRef.namespace,
    });
  };
}
